import {installUserProfileSync} from "./user_profile_sync"
// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html"
import {installSharedPlatformRuntime} from "./shared_platform_runtime"
// Establish Phoenix Socket and LiveView configuration.
import {Socket} from "phoenix"
import {LiveSocket} from "phoenix_live_view"
import topbar from "../vendor/topbar"

import ExecutionModal from "./hooks/execution_modal"

// Import hooks
import AppShellIsland from "./hooks/app_shell_island"
import AutoResize from "./hooks/auto_resize"
import LineClamp from "./hooks/line_clamp"
import PreserveDisclosure from "./hooks/preserve_disclosure"
import AutoScroll from "./hooks/auto_scroll"
import Clipboard from "./hooks/clipboard"
import ECharts from "./hooks/echarts"
import ToolEditor from "./hooks/tool_editor"
import BrandStylesheetEditor from "./hooks/brand_stylesheet_editor"
import MatthewDrawer from "./hooks/matthew_drawer"
import AppAgentLauncher from "./hooks/app_agent_launcher"
import MonicaModelMenu from "./hooks/monica_model_menu"
import SupportChat from "./hooks/support_chat"
import WsMonitor from "./hooks/ws_monitor"
import WebAuthn from "./hooks/webauthn"
import KanbanDrag from "./hooks/kanban_drag"
import AutoDismiss from "./hooks/auto_dismiss"
import CreditsCounter from "./hooks/credits_counter"
import DeckScale from "./hooks/deck_scale"
import CommitteeCouncil from "./hooks/committee_council"
import AppEditor from "./hooks/app_editor"
import SortableTabs from "./hooks/sortable_tabs"
import SortableCockpit from "./hooks/sortable_cockpit"
import SortableList from "./hooks/sortable_list"
import FileTreeDrag from "./hooks/file_tree_drag"
import ResizeHandle from "./hooks/resize_handle"
import CommitteeRoomMessage from "./hooks/committee_room_message"
import ChoiceConfirmDelay from "./hooks/choice_confirm_delay"
import CommitteeRoomTranscript from "./hooks/committee_room_transcript"
import CommitteeRoomSplit from "./hooks/committee_room_split"
import CommitteeContextStatus from "./hooks/committee_context_status"
import ChatMention from "./hooks/chat_mention"
import ChatSubmitClear from "./hooks/chat_submit_clear"
import DrawerMention from "./hooks/drawer_mention"
import SkillMention from "./hooks/skill_mention"
import LiveDuration from "./hooks/live_duration"
import ExecutionCollapse from "./hooks/execution_collapse"
import ColorSync from "./hooks/color_sync"
import ChatOpenSignal from "./hooks/chat_open_signal"
import WorkflowEditorBridge from "./hooks/workflow_editor_bridge"
import DiscoveryBoardBridge from "./hooks/discovery_board_bridge"
import ErDiagramBridge from "./hooks/er_diagram_bridge"
import SignedUpload from "./hooks/signed_upload"
import IframeErrorBridge from "./hooks/iframe_error_bridge"
import Download from "./hooks/download"
import LoginHero from "./hooks/login_hero"
import ThemeSelector from "./hooks/theme_selector"
import { EditLockHeartbeat } from "./hooks/edit_lock_heartbeat"
import { WidgetIframeAutoresize } from "./hooks/widget_iframe_autoresize"
import AudioRecorder from "./hooks/audio_recorder"
import DblClick from "./hooks/dbl_click"
import CommitteeShellNavigation from "./hooks/committee_shell_navigation"
import CommitteeResourceFrame from "./hooks/committee_resource_frame"
import { AgentSkillsFrame, AgentSkillsReturn } from "./hooks/agent_skills_frame"
import { AgentResourceFrame } from "./hooks/agent_resource_frame"
import CommitteeMonicaLauncher from "./hooks/committee_monica_launcher"
import SearchableSelect from "./hooks/searchable_select"
import ConsentWorkspaceSelect from "./hooks/consent_workspace_select"
import {preserveOverlayPromotion} from "./hooks/aios_overlays"
// Side-effect import: sets up dark mode listeners (no hook registration).
import "./hooks/theme_sync"

import { t } from "./i18n.js"
let csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content")
// Embed mode uses a separate cookie and a separate LiveView socket so its
// session is isolated from the standalone Seiva app. The embed_root layout
// emits a `seiva-embed` meta tag; the path prefix is no longer a reliable
// signal now that embed routes share the standalone tree.
let socketPath = document.querySelector("meta[name='seiva-embed']") ? "/embed-live" : "/live"
let liveSocket = new LiveSocket(socketPath, Socket, {
  // Give slow mounts (e.g. CommitteeRoom) more time to establish the WebSocket
  // before falling back to longpoll. Longpoll causes repeated remounts, which
  // in turn re-initializes the Matthew drawer and other stateful components.
  longPollFallbackMs: 30000,
  params: {_csrf_token: csrfToken},
  dom: {onBeforeElUpdated: preserveOverlayPromotion},
  hooks: {
    LineClamp,
    PreserveDisclosure,
    ExecutionModal,
    AutoResize,
    AutoScroll,
    Clipboard,
    ECharts,
    ToolEditor,
    BrandStylesheetEditor,
    AppShellIsland,
    MatthewDrawer,
    SupportChat,
    MonicaModelMenu,
    WsMonitor,
    WebAuthn,
    KanbanDrag,
    AutoDismiss,
    CreditsCounter,
    DeckScale,
    CommitteeCouncil,
    AppEditor,
    SortableTabs,
    SortableCockpit,
    SortableList,
    FileTreeDrag,
    ResizeHandle,
    CommitteeRoomMessage,
    ChoiceConfirmDelay,
    CommitteeRoomTranscript,
    CommitteeRoomSplit,
    CommitteeContextStatus,
    ChatMention,
    ChatSubmitClear,
    DrawerMention,
    SkillMention,
    LiveDuration,
    ExecutionCollapse,
    ColorSync,
    ChatOpenSignal,
    WorkflowEditorBridge,
    DiscoveryBoardBridge,
    ErDiagramBridge,
    SignedUpload,
    IframeErrorBridge,
    Download,
    LoginHero,
    ThemeSelector,
    EditLockHeartbeat,
    WidgetIframeAutoresize,
    AudioRecorder,
    DblClick,
    CommitteeShellNavigation,
    CommitteeResourceFrame,
    AgentSkillsFrame,
    AgentSkillsReturn,
    AgentResourceFrame,
    CommitteeMonicaLauncher,
    AppAgentLauncher,
    SearchableSelect,
    ConsentWorkspaceSelect,
  }
})

installSharedPlatformRuntime()

// Show progress bar on live navigation and form submits. Committee resources
// run the same LiveView client inside a same-origin iframe, but the outer shell
// owns navigation feedback. Suppress only the frame's page-level bar so the
// shell and the embedded resource do not render stacked progress indicators.
function isEmbeddedCommitteeSurface() {
  return (
    window.self !== window.parent &&
    new URLSearchParams(window.location.search).get("committee_surface") === "true"
  )
}

// The Monica/Support launchers of a Committee resource belong to the PARENT
// document and float over this frame's lower-right corner — exactly where a
// side drawer pins its footer. `app.css` reads this class to keep the footer
// actions clear of them (no z-index inside the frame can lift above them).
if (isEmbeddedCommitteeSurface()) {
  document.documentElement.classList.add("seiva-committee-frame")
}

topbar.config({barColors: {0: "#FF826B"}, shadowColor: "rgba(3, 47, 83, .24)"})
window.addEventListener("phx:page-loading-start", _info => {
  if (isEmbeddedCommitteeSurface()) {
    topbar.hide()
    return
  }

  topbar.show(300)
})
window.addEventListener("phx:page-loading-stop", _info => {
  if (!isEmbeddedCommitteeSurface()) topbar.hide()
})

window.addEventListener("phx:branding_cache_reset", (event) => {
  try {
    const workspaceId = event.detail && event.detail.workspace_id
    const prefixes = ["seiva_branding", "seiva_style_vars", "seiva_color_tokens"]

    Object.keys(localStorage).forEach((key) => {
      if (prefixes.some((prefix) => key === prefix || key.startsWith(`${prefix}:`) || key.startsWith(`${prefix}_`))) {
        localStorage.removeItem(key)
      }

      if (workspaceId && key.includes(workspaceId) && /brand|style|color/i.test(key)) {
        localStorage.removeItem(key)
      }
    })
  } catch (_error) {
    // Ignore storage errors; the reload below still refreshes server-rendered branding.
  }

  window.location.reload()
})

// connect if there are any LiveViews on the page
installUserProfileSync(window, document, liveSocket.socket)
liveSocket.connect()

// expose liveSocket on window for web console debug logs and latency simulation:
window.liveSocket = liveSocket

// ===== Sidebar collapse toggle =====
// Toggles between w-60 (expanded) and w-20 (collapsed) without a server round-trip.
// The sidebar uses CSS to animate labels and chevrons; JS only switches width.
window.addEventListener("seiva:toggle-sidebar", () => {
  const sidebar = document.getElementById("app-sidebar")
  const toggle = document.getElementById("sidebar-toggle")
  if (!sidebar) return

  const isCollapsed = sidebar.classList.contains("w-20")

  if (isCollapsed) {
    sidebar.classList.remove("w-20")
    sidebar.classList.add("w-60")
    document.documentElement.classList.add("seiva-sidebar-expanded")
    if (toggle) toggle.setAttribute("aria-label", t("Collapse sidebar"))
    localStorage.setItem("seiva_sidebar_collapsed", "false")
  } else {
    sidebar.classList.remove("w-60")
    sidebar.classList.add("w-20")
    document.documentElement.classList.remove("seiva-sidebar-expanded")
    if (toggle) toggle.setAttribute("aria-label", t("Expand sidebar"))
    localStorage.setItem("seiva_sidebar_collapsed", "true")
  }
})

// Sidebar always starts collapsed (w-20 in HTML) — no localStorage restore
document.documentElement.classList.remove("seiva-sidebar-expanded")

// ===== Mobile off-canvas sidebar drawer =====
// Hamburger button uses JS.add_class directly (in HEEx) for the open state.
// These listeners handle automatic closing: backdrop click (also via JS.remove_class),
// Esc key, navigation, sidebar nav link click, and viewport growing past lg.
function closeMobileSidebar() {
  const sidebar = document.getElementById("app-sidebar") || document.getElementById("partnership-sidebar")
  const backdrop = document.getElementById("sidebar-backdrop")
  if (sidebar) sidebar.classList.remove("mobile-open")
  if (backdrop) backdrop.classList.remove("visible")
  document.body.classList.remove("overflow-hidden")
}

// Esc key closes the mobile drawer
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") closeMobileSidebar()
})

// Auto-close on navigation (LiveView page transitions)
window.addEventListener("phx:page-loading-stop", () => closeMobileSidebar())

// Auto-close when user taps any sidebar nav link (so SPA-style nav closes the drawer)
document.addEventListener("click", (e) => {
  const sidebar = document.getElementById("app-sidebar") || document.getElementById("partnership-sidebar")
  if (!sidebar || !sidebar.classList.contains("mobile-open")) return
  const link = e.target.closest("a[href], a[data-phx-link]")
  if (link && sidebar.contains(link)) closeMobileSidebar()
})

// Auto-close when viewport grows past lg breakpoint
const lgMedia = window.matchMedia("(min-width: 1024px)")
const onLgChange = (e) => { if (e.matches) closeMobileSidebar() }
if (lgMedia.addEventListener) lgMedia.addEventListener("change", onLgChange)
else if (lgMedia.addListener) lgMedia.addListener(onLgChange)

// ===== Sidebar collapsible sections (accordion) =====
window.toggleSidebarSection = function(section) {
  const content = document.querySelector(`[data-section-content="${section}"]`)
  const chevron = document.querySelector(`[data-chevron="${section}"]`)
  if (!content) return

  const isCollapsed = content.classList.contains("collapsed")

  if (isCollapsed) {
    content.classList.remove("collapsed")
    content.classList.add("expanded")
    content.style.maxHeight = content.scrollHeight + "px"
    if (chevron) chevron.classList.remove("rotated")
  } else {
    content.style.maxHeight = content.scrollHeight + "px"
    // Force reflow
    content.offsetHeight
    content.classList.add("collapsed")
    content.classList.remove("expanded")
    content.style.maxHeight = "0px"
    if (chevron) chevron.classList.add("rotated")
  }

  // Persist state
  const collapsed = JSON.parse(localStorage.getItem("seiva_sidebar_sections") || "{}")
  collapsed[section] = !isCollapsed
  localStorage.setItem("seiva_sidebar_sections", JSON.stringify(collapsed))
}

// Restore collapsed sidebar sections on page load
document.addEventListener("DOMContentLoaded", () => {
  const collapsed = JSON.parse(localStorage.getItem("seiva_sidebar_sections") || "{}")
  Object.entries(collapsed).forEach(([section, isCollapsed]) => {
    if (isCollapsed) {
      const content = document.querySelector(`[data-section-content="${section}"]`)
      const chevron = document.querySelector(`[data-chevron="${section}"]`)
      if (content) {
        content.classList.add("collapsed")
        content.classList.remove("expanded")
        content.style.maxHeight = "0px"
      }
      if (chevron) chevron.classList.add("rotated")
    }
  })
})
